/**
 * 全网影视热度榜弹窗（heatchart 批 2026-09-30，只读）。样板 = CareerRecordPanel：
 * modal-wrap + modal-note-card 独立覆盖层，⛔ 不入 app 的 panels 注册表。
 *
 * 展示时点（2026-09-30 用户定）：每月决策日入场，**行程结束 → 榜单弹窗 → 小助理播报**——
 * 开窗前 app 先 AssistPanel.hideVn() 把播报整段押后（不记已读），关窗回调交还 app 续链
 * （恢复播报 → 颁奖特例/结局判定 → 决策日）。
 *
 * 行结构（评分⛔上屏，用户定）：排名 / 剧名 / 类型 chip / 「爆」标；次行 主演 + 热度 + 上月对照
 * （↑n ↓n 新上榜）。第 1 名 ✦ 金饰；玩家在播行加「你」chip；TOP5 之外只给玩家专属行（真实名次）。
 * 剧名/类型读现表（榜单=当下市场，⛔ 履历那套定格快照哲学）。
 */
import { GameState } from '../../systems/GameState';
import { GameData } from '../../data/GameData';
import { heatChartSystem } from '../../systems/HeatChartSystem';
import { absMonthOf } from '../../data/tables/Awards';
import { HeatChartRow, ScriptGenre } from '../../data/types';
import { createDiv, createModalDismiss, createSpan } from '../ui';

/** 剧本类型中文名（与 CareerRecordPanel 同一套叫法，各自局部表 ⛔ 为一个小映射去改公共处） */
const GENRE_CN: Record<ScriptGenre, string> = {
    movie: '电影', tv: '电视剧', variety: '综艺', web: '网剧', short: '短剧', other: '其他',
};

export class HeatChartPanel {
    readonly element: HTMLElement;
    private readonly titleEl: HTMLElement;
    private readonly body: HTMLElement;
    /** 关窗后续行动作（app 注入：恢复播报 → 决策日尾链） */
    onClose: (() => void) | null = null;

    constructor() {
        const wrap = createDiv('modal-wrap heatchart-pop');
        const card = createDiv('modal-note-card heatchart-card');
        this.titleEl = createDiv('mtitle', ['']);
        card.appendChild(this.titleEl);
        this.body = createDiv('heatchart-body');
        card.appendChild(this.body);
        // 2026-10-16 弹窗统一批（茄子令）：卡内那颗「关闭」删掉，关闭热区＝遮罩本体（见 ui.ts createModalDismiss）
        wrap.appendChild(createModalDismiss(() => this.close()));
        wrap.appendChild(card);

        this.element = wrap;
    }

    get isOpen(): boolean {
        return this.element.classList.contains('show');
    }

    open(): void {
        if (this.isOpen) return;
        this.render();
        this.element.classList.add('show');
        heatChartSystem.markChartShown(); // 落章在开窗这一刻：同月内切页签回来⛔ 重弹
    }

    close(): void {
        if (!this.isOpen) return;
        this.element.classList.remove('show');
        this.onClose?.();
    }

    private render(): void {
        const p = GameState.getInstance().player;
        this.body.innerHTML = '';
        // 标题=「N月全网影视热度榜」：只显示月份不带年份、无间隔点（2026-09-30 用户定）。
        // 归属月 = **刚结束的行程月**（2026-10-01 用户实测纠偏：3 月底行程结束、4 月初决策日看到的
        // 榜单是 3 月末的市场快照，应叫「3月榜」而非「4月榜」）——决策日入场定格（seenAbs=当月），
        // 归属月取 seenAbs 的上一自然月
        // 归属月 = seenAbs 的上一自然月；absMonth→month 换算里余 0 = 12 月（abs=37 即 Y4M1 ⇒ 归属 Y3M12）
        const prevAbs = p.heatChart ? p.heatChart.seenAbs - 1 : absMonthOf(p.year, p.month) - 1;
        const m = ((prevAbs % 12) + 12) % 12;
        const chartMonth = m === 0 ? 12 : m;
        this.titleEl.textContent = `${chartMonth}月全网影视热度榜`;

        const rows = heatChartSystem.current()?.rows ?? [];
        const tops = rows.filter(r => !r.outOfTop);
        const outs = rows.filter(r => r.outOfTop);
        if (!tops.length) {
            this.body.appendChild(createDiv('career-empty', ['本月没有在播或上映的作品。']));
            return;
        }
        tops.forEach(r => this.body.appendChild(rowEl(r)));
        for (const r of outs) {
            this.body.appendChild(createDiv('heatchart-out',
                [`✦ 你参演的《${workName(r)}》排在第 ${r.rank} 位`]));
        }
    }
}

// ===== 行构造（纯函数） =====

function workName(r: HeatChartRow): string {
    return GameData.SCRIPT_MAP[r.scriptId]?.name ?? '未知作品';
}

function rowEl(r: HeatChartRow): HTMLElement {
    const item = createDiv(`career-item heatchart-row${r.rank === 1 ? ' top1' : ''}`);
    const line1: (HTMLElement | string)[] = [createSpan('heatchart-rank', String(r.rank))];
    if (r.rank === 1) line1.push(createSpan('heatchart-star', '✦')); // 柔金只作装饰字形（--starGold ⛔ 作正文色）
    line1.push(createSpan('career-title', `《${workName(r)}》`));
    line1.push(createSpan('career-chip', GENRE_CN[r.genre] ?? r.genre));
    if (r.mine) line1.push(createSpan('career-chip', '参演')); // ⛔「你」：参演措辞（2026-10-01 用户定）
    if (r.boom) line1.push(createSpan('heatchart-boom', '爆'));
    item.appendChild(createDiv('career-line', line1));

    const delta = r.prevRank === 0
        ? createSpan('heatchart-delta flat', '新上榜')
        : r.prevRank > r.rank
            ? createSpan('heatchart-delta up', `↑${r.prevRank - r.rank}`)
            : r.prevRank < r.rank
                ? createSpan('heatchart-delta down', `↓${r.rank - r.prevRank}`)
                : createSpan('heatchart-delta flat', '—');
    item.appendChild(createDiv('career-line', [
        createSpan('career-sub', r.stars.length ? `主演 ${r.stars.join(' · ')}` : ''),
        createSpan('career-nums', `热度 ${r.heat}`),
        delta,
    ]));
    return item;
}
